import type { ContactViewWire } from '@badger-sentinel/protocol';

/**
 * Publishing a contact to the rest of the team (RB §2.4, §5.8.4).
 *
 * An SDA or ISR seat sees contacts nobody else on its team can
 * (`capabilities.seesUnpublishedContacts`); publishing is the act that hands one
 * over, and the server re-snapshots the whole team on it. It is a **command, not
 * an order** — like the MW dispositions, it changes the live world now rather
 * than at resolution — so it carries no confirm and no queue entry.
 *
 * ## Three states, and only one of them is a control
 *
 * A contact already published states its publisher and offers nothing: the row
 * `PUBLISHED BY · SDA` is in `contactRows` and repeating it as a disabled button
 * would be the console saying one thing twice. A seat without `canPublish` gets
 * nothing at all, because a control it cannot use is a control that teaches the
 * console is broken. The button appears for exactly the seat that can act.
 *
 * It sits in the selection rail rather than on the SDA board because the subject
 * is a *contact*, and an ISR seat publishes a `REGION` contact off the theatre
 * map with the same gesture. One control, wherever the contact was selected.
 */
export function PublishControl(props: {
  readonly contact: ContactViewWire;
  readonly canPublish: boolean;
  readonly onPublish: (contact: string) => void;
}): JSX.Element | null {
  if (!props.canPublish || props.contact.publishedBy !== null) return null;
  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        padding: '8px 12px',
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <button
        type="button"
        data-testid="publish-contact"
        onClick={() => props.onPublish(props.contact.id)}
        style={{
          flex: 1,
          height: 28,
          background: 'transparent',
          border: '1px solid var(--primary)',
          color: 'var(--primary)',
          fontFamily: 'var(--font-ui)',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        }}
      >
        PUBLISH TO TEAM
      </button>
    </div>
  );
}
